import { useCallback, useEffect, useMemo, useState } from "react";
import { Lock, RefreshCw, Download, LogOut, Search, Mail, MessageSquare, ClipboardList, Check, Clock, X, Trash2, AlertTriangle } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import AmbientParticles from "@/components/site/AmbientParticles";

const TOKEN_KEY = "esthera_admin_token";

const TABS = [
  { key: "demo",    label: "Demo & Audit Requests", endpoint: "/api/demo-requests",    icon: ClipboardList },
  { key: "contact", label: "Contact Messages",      endpoint: "/api/contact-messages", icon: MessageSquare },
];

function SyncStepper({ lead, collection, token, onRetryOptimistic }) {
  const status = lead.sync_status;

  // Legacy records (created before the Airtable integration) — no field, quiet placeholder.
  if (!status) return <span className="text-platinum/25 text-[12px]" data-testid="sync-badge-none">—</span>;

  // Palette per the build brief — success/warning/error tokens from the design system.
  const OUTCOME = {
    synced:  { color: "#22C55E", icon: Check, label: "Synced",  line: "solid",  testId: "sync-badge-synced" },
    pending: { color: "#F59E0B", icon: Clock, label: "Pending", line: "dashed", testId: "sync-badge-pending", pulse: true },
    failed:  { color: "#EF4444", icon: X,     label: "Failed",  line: "dotted", testId: "sync-badge-failed" },
  };
  const spec = OUTCOME[status] || OUTCOME.failed;
  const Node2Icon = spec.icon;

  const ts = lead.sync_updated_at ? new Date(lead.sync_updated_at).toLocaleString() : null;
  const tooltip = status === "pending"
    ? "Awaiting sync…"
    : status === "synced"
      ? `Synced at ${ts || "—"}`
      : `Failed at ${ts || "—"}${lead.sync_error ? "\n\n" + lead.sync_error : ""}`;

  const retry = async () => {
    onRetryOptimistic(lead.id);
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/retry-sync/${collection}/${lead.id}`,
        { method: "POST", headers: { "X-Admin-Token": token } }
      );
    } catch { /* if the retry endpoint call itself fails, the next Refresh shows real state */ }
  };

  const linePattern = spec.line === "dashed" ? "4px" : "2px";
  const lineGap     = spec.line === "dashed" ? "8px" : "4px";

  return (
    <div className="inline-flex items-center gap-2" data-testid={spec.testId}>
      {/* Node 1 — Submitted (neutral, always filled) */}
      <span title="Submitted" className="w-2 h-2 rounded-full shrink-0"
        style={{ background: "rgba(230,232,236,0.55)" }} />
      {/* Connecting line */}
      <span className="inline-block h-px w-6 shrink-0"
        style={{
          background: spec.line === "solid"
            ? spec.color
            : `repeating-linear-gradient(to right, ${spec.color} 0, ${spec.color} ${linePattern}, transparent ${linePattern}, transparent ${lineGap})`,
          opacity: spec.line === "dotted" ? 0.8 : 1,
        }} />
      {/* Node 2 — outcome */}
      <span
        title={tooltip}
        className={`relative inline-flex items-center justify-center rounded-full w-4 h-4 shrink-0 ${spec.pulse ? "sync-node-pulse" : ""}`}
        style={{ background: spec.color, boxShadow: `0 0 0 3px ${spec.color}22` }}
      >
        <Node2Icon className="w-2.5 h-2.5 text-obsidian" strokeWidth={3.5} />
      </span>
      {/* Label */}
      <span className="text-[11.5px] font-medium tracking-[0.02em]" style={{ color: spec.color }}>
        {spec.label}
      </span>
      {/* Retry — Failed only */}
      {status === "failed" && (
        <button
          type="button"
          onClick={retry}
          title="Retry sync"
          data-testid="sync-retry-btn"
          className="ml-1 w-6 h-6 rounded-md inline-flex items-center justify-center text-sapphire-glow hover:text-white transition-colors"
          style={{ background: "rgba(37,99,235,0.12)", border: "1px solid rgba(37,99,235,0.35)" }}
        >
          <RefreshCw className="w-3 h-3" strokeWidth={2.4} />
        </button>
      )}
    </div>
  );
}

export default function AdminLeadsPage() {
  const [token, setToken]     = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [pwInput, setPwInput] = useState("");
  const [activeTab, setActiveTab] = useState("demo");
  const [demoLeads, setDemoLeads] = useState([]);
  const [contacts, setContacts]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [q, setQ]             = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);  // single-record: { collection, id, email, full_name } | bulk: { collection, ids, count } | null
  const [deleteBusy, setDeleteBusy]       = useState(false);
  const [selectedIds, setSelectedIds]     = useState(new Set()); // ids currently checked in the ACTIVE tab

  const loadTab = useCallback(async (tk, tabKey) => {
    const tab = TABS.find((t) => t.key === tabKey);
    if (!tab) return;
    setLoading(true); setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${tab.endpoint}`, { headers: { "X-Admin-Token": tk } });
      if (res.status === 401) { setError("Invalid password."); setDemoLeads([]); setContacts([]); setToken(""); localStorage.removeItem(TOKEN_KEY); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (tabKey === "demo") setDemoLeads(data || []);
      else setContacts(data || []);
    } catch (e) { setError("Could not load records. Try again."); }
    finally { setLoading(false); }
  }, []);

  const loadAll = useCallback(async (tk) => {
    await Promise.all([loadTab(tk, "demo"), loadTab(tk, "contact")]);
  }, [loadTab]);

  useEffect(() => { if (token) loadAll(token); }, [token, loadAll]);

  const signIn = (e) => {
    e.preventDefault();
    if (!pwInput.trim()) return;
    localStorage.setItem(TOKEN_KEY, pwInput.trim());
    setToken(pwInput.trim());
  };

  const signOut = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); setDemoLeads([]); setContacts([]); };

  // Optimistic UI: when Retry is clicked, immediately flip local sync_status → "pending".
  // The real state gets refreshed on the next manual Refresh button click.
  const flipToPending = (id) => {
    const map = (list) => list.map((l) => l.id === id ? { ...l, sync_status: "pending", sync_error: null } : l);
    setDemoLeads((l) => map(l));
    setContacts((l) => map(l));
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    try {
      if (pendingDelete.kind === "bulk") {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${pendingDelete.collection}/bulk`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json", "X-Admin-Token": token },
            body: JSON.stringify({ record_ids: pendingDelete.ids }),
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const idSet = new Set(pendingDelete.ids);
        const drop = (list) => list.filter((l) => !idSet.has(l.id));
        setDemoLeads(drop);
        setContacts(drop);
        setSelectedIds(new Set());
      } else {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${pendingDelete.collection}/${pendingDelete.id}`,
          { method: "DELETE", headers: { "X-Admin-Token": token } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const drop = (list) => list.filter((l) => l.id !== pendingDelete.id);
        setDemoLeads(drop);
        setContacts(drop);
      }
      setPendingDelete(null);
    } catch (e) {
      setError("Delete failed — try again.");
    } finally {
      setDeleteBusy(false);
    }
  };

  // Reset selection when the tab changes so we never bulk-delete across tabs by accident
  useEffect(() => { setSelectedIds(new Set()); }, [activeTab]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = (visibleIds) => {
    setSelectedIds((prev) => {
      const allChecked = visibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allChecked) visibleIds.forEach((id) => next.delete(id));
      else            visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const items = activeTab === "demo" ? demoLeads : contacts;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    const fields = activeTab === "demo"
      ? ["full_name", "email", "company", "role", "phone", "team_size", "message"]
      : ["first_name", "last_name", "email", "company", "phone", "inquiry_type", "message"];
    return items.filter((l) =>
      fields.map((f) => l[f]).filter(Boolean).some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [items, q, activeTab]);

  const freshCount = useMemo(() => {
    const dayAgo = Date.now() - 24 * 3600 * 1000;
    return items.filter((l) => l.created_at && new Date(l.created_at).getTime() >= dayAgo).length;
  }, [items]);

  const exportCsv = () => {
    const cols = activeTab === "demo"
      ? ["created_at", "full_name", "email", "company", "role", "team_size", "phone", "message"]
      : ["created_at", "first_name", "last_name", "email", "company", "phone", "inquiry_type", "message"];
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = [cols.join(",")].concat(filtered.map((l) => cols.map((c) => esc(l[c])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `esthera-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden flex flex-col">
        <div className="fixed inset-0 pointer-events-none z-0" aria-hidden><AmbientParticles count={26} tone="sapphire" density={0.45} /></div>
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6">
          <form onSubmit={signIn} data-testid="admin-login"
            className="relative w-full max-w-[420px] rounded-2xl border border-white/[0.08] p-8 fade-up"
            style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.7) 0%, rgba(10,15,26,0.92) 100%)" }}>
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/55 to-transparent" />
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full inline-flex items-center justify-center"
                style={{ background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.14), #0E1422 75%)", border: "1px solid rgba(96,165,250,0.55)" }}>
                <Lock className="w-4 h-4 text-sapphire-glow" />
              </div>
              <div>
                <div className="eyebrow-intelligence">PRIVATE</div>
                <div className="font-display text-platinum text-[20px] tracking-tight">Lead Inbox</div>
              </div>
            </div>
            <p className="text-[12.5px] text-platinum/60 leading-[1.6] mb-5">Enter the admin password to view submitted demo, audit, and contact messages.</p>
            <input type="password" placeholder="Admin password" autoFocus required
              className="w-full h-12 rounded-md bg-[rgba(10,15,26,0.7)] border border-white/10 px-4 text-[14px] text-platinum placeholder:text-platinum/40 focus:outline-none focus:border-sapphire-glow/55 transition-colors"
              value={pwInput} onChange={(e) => setPwInput(e.target.value)} data-testid="admin-password" />
            {error && <div className="mt-3 text-[12px] text-red-400/85">{error}</div>}
            <button type="submit" data-testid="admin-signin"
              className="btn-primary rounded-md h-11 w-full mt-5 text-[13px] font-medium inline-flex items-center justify-center gap-2">
              Sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="admin-leads-page">
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden><AmbientParticles count={26} tone="sapphire" density={0.45} /></div>
      <Navbar />

      <section className="relative pt-[120px] lg:pt-[140px] pb-20">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          {/* Header bar */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <div className="fade-up">
              <div className="eyebrow-intelligence mb-2">PRIVATE · LEAD INBOX</div>
              <h1 className="font-display text-platinum text-[30px] sm:text-[36px] lg:text-[42px] leading-tight tracking-[-0.018em]">
                {TABS.find((t) => t.key === activeTab)?.label}
              </h1>
              <p className="mt-2 text-[13px] text-platinum/60">
                {items.length} {items.length === 1 ? "record" : "records"} captured · sorted by newest first
                {freshCount > 0 && (
                  <span data-testid="admin-fresh-count" className="ml-2.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] tracking-[0.12em] uppercase font-medium"
                    style={{
                      background: "rgba(96,165,250,0.14)",
                      border: "1px solid rgba(96,165,250,0.45)",
                      color: "#93C5FD",
                      boxShadow: "0 0 14px -3px rgba(96,165,250,0.5)",
                    }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-sapphire-glow" style={{ boxShadow: "0 0 8px rgba(96,165,250,1)" }} />
                    +{freshCount} new today
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2.5 fade-up" style={{ animationDelay: "80ms" }}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-platinum/45" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="admin-search"
                  className="h-10 w-[220px] rounded-md bg-[rgba(10,15,26,0.7)] border border-white/10 pl-9 pr-3 text-[13px] text-platinum placeholder:text-platinum/40 focus:outline-none focus:border-sapphire-glow/55" />
              </div>
              <button onClick={() => loadTab(token, activeTab)} disabled={loading} data-testid="admin-refresh"
                className="btn-ghost rounded-md h-10 px-3 text-[12px] font-medium inline-flex items-center gap-1.5">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
              <button onClick={exportCsv} disabled={!filtered.length} data-testid="admin-export-csv"
                className="btn-primary rounded-md h-10 px-3.5 text-[12px] font-medium inline-flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
              <button onClick={signOut} data-testid="admin-signout"
                className="btn-ghost rounded-md h-10 px-3 text-[12px] inline-flex items-center gap-1.5">
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1.5 mb-5 p-1.5 rounded-xl border border-white/[0.07] w-fit fade-up"
            style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.6) 0%, rgba(10,15,26,0.9) 100%)" }}>
            {TABS.map((t) => {
              const active = t.key === activeTab;
              const count  = t.key === "demo" ? demoLeads.length : contacts.length;
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`admin-tab-${t.key}`}
                  className={`h-9 px-4 rounded-lg text-[12px] tracking-[0.10em] uppercase font-medium inline-flex items-center gap-2 transition-all ${active ? "text-white" : "text-platinum/60 hover:text-white"}`}
                  style={active ? {
                    background: "linear-gradient(180deg, rgba(96,165,250,0.95), rgba(37,99,235,0.95))",
                    border: "1px solid rgba(147,197,253,0.55)",
                    boxShadow: "0 0 16px rgba(96,165,250,0.45), inset 0 1px 0 rgba(255,255,255,0.18)",
                  } : { background: "transparent" }}>
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                  <span className={`ml-1 rounded-full px-1.5 h-4 min-w-[18px] inline-flex items-center justify-center text-[10px] font-semibold ${active ? "bg-white/25 text-white" : "bg-white/10 text-platinum/65"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Bulk-select bar — visible when ≥1 row selected on the current tab */}
          {(() => {
            const currentCollection = activeTab === "demo" ? "demo_requests" : "contact_messages";
            const visibleSelected = filtered.filter((l) => selectedIds.has(l.id));
            if (visibleSelected.length === 0) return null;
            return (
              <div className="mb-3 flex items-center justify-between rounded-lg border border-sapphire-glow/40 px-4 py-2.5 fade-up"
                style={{ background: "linear-gradient(180deg, rgba(37,99,235,0.14) 0%, rgba(13,20,42,0.85) 100%)" }}
                data-testid="admin-bulk-bar">
                <div className="text-[12.5px] text-platinum">
                  <span className="text-sapphire-glow font-semibold">{visibleSelected.length}</span> selected
                  <button type="button" onClick={() => setSelectedIds(new Set())}
                    data-testid="admin-bulk-clear"
                    className="ml-3 text-[11.5px] text-platinum/55 hover:text-white underline decoration-dotted underline-offset-4">
                    Clear selection
                  </button>
                </div>
                <button type="button"
                  data-testid="admin-bulk-delete"
                  onClick={() => setPendingDelete({ kind: "bulk", collection: currentCollection, ids: visibleSelected.map((l) => l.id), count: visibleSelected.length })}
                  className="h-9 px-3.5 rounded-md text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors"
                  style={{ background: "linear-gradient(180deg, #DC2626 0%, #B91C1C 100%)", color: "#FEF2F2", border: "1px solid rgba(248,113,113,0.55)" }}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete selected ({visibleSelected.length})
                </button>
              </div>
            );
          })()}

          {/* Table */}
          <div className="relative rounded-2xl border border-white/[0.07] overflow-hidden"
            style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.55) 0%, rgba(10,15,26,0.92) 100%)" }}>
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />
            <div className="overflow-x-auto">
              {activeTab === "demo" ? (
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-[10px] tracking-[0.18em] uppercase text-platinum/45 border-b border-white/[0.06]">
                      <th className="w-10 px-4 py-3.5">
                        <input type="checkbox"
                          data-testid="admin-select-all-demo"
                          checked={filtered.length > 0 && filtered.every((l) => selectedIds.has(l.id))}
                          onChange={() => toggleAllVisible(filtered.map((l) => l.id))}
                          className="w-3.5 h-3.5 accent-sapphire-glow cursor-pointer"
                          aria-label="Select all visible" />
                      </th>
                      <th className="text-left font-medium px-5 py-3.5">When</th>
                      <th className="text-left font-medium px-5 py-3.5">Name</th>
                      <th className="text-left font-medium px-5 py-3.5">Email</th>
                      <th className="text-left font-medium px-5 py-3.5">Company</th>
                      <th className="text-left font-medium px-5 py-3.5">Role</th>
                      <th className="text-left font-medium px-5 py-3.5">Size</th>
                      <th className="text-left font-medium px-5 py-3.5">Phone</th>
                      <th className="text-left font-medium px-5 py-3.5">Message</th>
                      <th className="text-left font-medium px-5 py-3.5">Sync</th>
                      <th className="text-right font-medium px-5 py-3.5 w-14">Del</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l, i) => {
                      const isFresh = l.created_at && (Date.now() - new Date(l.created_at).getTime()) < 24 * 3600 * 1000;
                      const checked = selectedIds.has(l.id);
                      return (
                        <tr key={l.id || i} data-testid={`admin-lead-${i}`} data-fresh={isFresh ? "true" : "false"}
                          className={`border-b border-white/[0.04] hover:bg-white/[0.025] transition-colors ${checked ? "bg-sapphire-glow/[0.06]" : ""}`}
                          style={isFresh && !checked ? { background: "linear-gradient(90deg, rgba(96,165,250,0.07) 0%, rgba(96,165,250,0.02) 60%, transparent 100%)" } : undefined}>
                          <td className="px-4 py-4 align-middle">
                            <input type="checkbox"
                              data-testid={`admin-lead-select-${i}`}
                              checked={checked}
                              onChange={() => toggleOne(l.id)}
                              className="w-3.5 h-3.5 accent-sapphire-glow cursor-pointer"
                              aria-label="Select row" />
                          </td>
                          <td className="px-5 py-4 text-platinum/65 whitespace-nowrap text-[12px]">
                            <div className="flex items-center gap-2">
                              {isFresh && (
                                <span title="New within 24 hours" className="w-1.5 h-1.5 rounded-full bg-sapphire-glow shrink-0"
                                  style={{ boxShadow: "0 0 10px rgba(96,165,250,1), 0 0 18px rgba(96,165,250,0.55)" }} />
                              )}
                              {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-platinum">{l.full_name || "—"}</td>
                          <td className="px-5 py-4">
                            <a href={`mailto:${l.email}`} className="text-sapphire-glow hover:underline inline-flex items-center gap-1.5">
                              <Mail className="w-3 h-3 opacity-70" />{l.email}
                            </a>
                          </td>
                          <td className="px-5 py-4 text-platinum/80">{l.company || "—"}</td>
                          <td className="px-5 py-4 text-platinum/65">{l.role || "—"}</td>
                          <td className="px-5 py-4 text-platinum/65 whitespace-nowrap">{l.team_size || "—"}</td>
                          <td className="px-5 py-4 text-platinum/65 whitespace-nowrap">{l.phone || "—"}</td>
                          <td className="px-5 py-4 text-platinum/60 max-w-[280px] truncate" title={l.message || ""}>{l.message || "—"}</td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <SyncStepper lead={l} collection="demo_requests" token={token} onRetryOptimistic={flipToPending} />
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-right">
                            <button type="button" title="Delete record" data-testid={`admin-lead-delete-${i}`}
                              onClick={() => setPendingDelete({ collection: "demo_requests", id: l.id, email: l.email, full_name: l.full_name })}
                              className="w-7 h-7 rounded-md inline-flex items-center justify-center text-platinum/40 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/40 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {!filtered.length && (
                      <tr><td colSpan={11} className="px-5 py-12 text-center text-platinum/55 text-[13px]">
                        {loading ? "Loading…" : q ? "No matches for your search." : "No leads yet."}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-[10px] tracking-[0.18em] uppercase text-platinum/45 border-b border-white/[0.06]">
                      <th className="w-10 px-4 py-3.5">
                        <input type="checkbox"
                          data-testid="admin-select-all-contact"
                          checked={filtered.length > 0 && filtered.every((l) => selectedIds.has(l.id))}
                          onChange={() => toggleAllVisible(filtered.map((l) => l.id))}
                          className="w-3.5 h-3.5 accent-sapphire-glow cursor-pointer"
                          aria-label="Select all visible" />
                      </th>
                      <th className="text-left font-medium px-5 py-3.5">When</th>
                      <th className="text-left font-medium px-5 py-3.5">Name</th>
                      <th className="text-left font-medium px-5 py-3.5">Email</th>
                      <th className="text-left font-medium px-5 py-3.5">Company</th>
                      <th className="text-left font-medium px-5 py-3.5">Inquiry</th>
                      <th className="text-left font-medium px-5 py-3.5">Phone</th>
                      <th className="text-left font-medium px-5 py-3.5">Message</th>
                      <th className="text-left font-medium px-5 py-3.5">Sync</th>
                      <th className="text-right font-medium px-5 py-3.5 w-14">Del</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l, i) => {
                      const isFresh = l.created_at && (Date.now() - new Date(l.created_at).getTime()) < 24 * 3600 * 1000;
                      const fullName = [l.first_name, l.last_name].filter(Boolean).join(" ") || "—";
                      const checked = selectedIds.has(l.id);
                      return (
                        <tr key={l.id || i} data-testid={`admin-contact-${i}`} data-fresh={isFresh ? "true" : "false"}
                          className={`border-b border-white/[0.04] hover:bg-white/[0.025] transition-colors ${checked ? "bg-sapphire-glow/[0.06]" : ""}`}
                          style={isFresh && !checked ? { background: "linear-gradient(90deg, rgba(96,165,250,0.07) 0%, rgba(96,165,250,0.02) 60%, transparent 100%)" } : undefined}>
                          <td className="px-4 py-4 align-middle">
                            <input type="checkbox"
                              data-testid={`admin-contact-select-${i}`}
                              checked={checked}
                              onChange={() => toggleOne(l.id)}
                              className="w-3.5 h-3.5 accent-sapphire-glow cursor-pointer"
                              aria-label="Select row" />
                          </td>
                          <td className="px-5 py-4 text-platinum/65 whitespace-nowrap text-[12px]">
                            <div className="flex items-center gap-2">
                              {isFresh && (
                                <span title="New within 24 hours" className="w-1.5 h-1.5 rounded-full bg-sapphire-glow shrink-0"
                                  style={{ boxShadow: "0 0 10px rgba(96,165,250,1), 0 0 18px rgba(96,165,250,0.55)" }} />
                              )}
                              {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-platinum">{fullName}</td>
                          <td className="px-5 py-4">
                            <a href={`mailto:${l.email}`} className="text-sapphire-glow hover:underline inline-flex items-center gap-1.5">
                              <Mail className="w-3 h-3 opacity-70" />{l.email}
                            </a>
                          </td>
                          <td className="px-5 py-4 text-platinum/80">{l.company || "—"}</td>
                          <td className="px-5 py-4 text-platinum/65">{l.inquiry_type || "—"}</td>
                          <td className="px-5 py-4 text-platinum/65 whitespace-nowrap">{l.phone || "—"}</td>
                          <td className="px-5 py-4 text-platinum/60 max-w-[320px] truncate" title={l.message || ""}>{l.message || "—"}</td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <SyncStepper lead={l} collection="contact_messages" token={token} onRetryOptimistic={flipToPending} />
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-right">
                            <button type="button" title="Delete record" data-testid={`admin-contact-delete-${i}`}
                              onClick={() => setPendingDelete({ collection: "contact_messages", id: l.id, email: l.email, full_name: fullName })}
                              className="w-7 h-7 rounded-md inline-flex items-center justify-center text-platinum/40 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/40 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {!filtered.length && (
                      <tr><td colSpan={10} className="px-5 py-12 text-center text-platinum/55 text-[13px]">
                        {loading ? "Loading…" : q ? "No matches for your search." : "No messages yet."}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          {error && <div className="mt-4 text-[12px] text-red-400/85">{error}</div>}
        </div>
      </section>

      {/* Delete confirmation modal — handles single-record and bulk delete */}
      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(5,8,15,0.72)", backdropFilter: "blur(6px)" }}
          onClick={() => !deleteBusy && setPendingDelete(null)}
          data-testid="delete-confirm-modal"
        >
          <div
            className="relative w-full max-w-md rounded-2xl border border-white/[0.08] p-6 fade-up"
            style={{ background: "linear-gradient(180deg, rgba(15,20,36,0.98) 0%, rgba(10,15,26,1) 100%)", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6), 0 0 40px rgba(220,38,38,0.05)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-red-500/50 to-transparent" />
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
                style={{ background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.35)" }}>
                <AlertTriangle className="w-4 h-4 text-red-400" strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-platinum text-[18px] leading-[1.25] tracking-[-0.01em]">
                  {pendingDelete.kind === "bulk"
                    ? `Delete ${pendingDelete.count} record${pendingDelete.count === 1 ? "" : "s"}?`
                    : "Delete this record?"}
                </div>
                <div className="mt-1.5 text-[13px] text-platinum/60 leading-[1.55]">
                  {pendingDelete.kind === "bulk"
                    ? `This removes ${pendingDelete.count} record${pendingDelete.count === 1 ? "" : "s"} from the lead inbox permanently.`
                    : "This removes the record from the lead inbox permanently."}
                  {" "}
                  <span className="text-platinum/80">It does not delete anything from Airtable</span> — that&rsquo;s a separate manual step.
                </div>
                {pendingDelete.kind !== "bulk" && (
                  <div className="mt-3 rounded-md px-3 py-2 text-[12.5px] text-platinum/75"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                    <div className="text-platinum">{pendingDelete.full_name || "—"}</div>
                    <div className="text-platinum/50 text-[11.5px] mt-0.5 truncate">{pendingDelete.email}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                disabled={deleteBusy}
                data-testid="delete-cancel"
                className="btn-ghost h-9 px-4 rounded-md text-[12.5px]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteBusy}
                data-testid="delete-confirm"
                className="h-9 px-4 rounded-md text-[12.5px] font-medium inline-flex items-center gap-1.5 transition-colors disabled:opacity-70"
                style={{ background: "linear-gradient(180deg, #DC2626 0%, #B91C1C 100%)", color: "#FEF2F2", border: "1px solid rgba(248,113,113,0.55)" }}
              >
                {deleteBusy
                  ? "Deleting…"
                  : (<><Trash2 className="w-3.5 h-3.5" />{pendingDelete.kind === "bulk" ? `Delete ${pendingDelete.count} record${pendingDelete.count === 1 ? "" : "s"}` : "Delete record"}</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

